<template>

    <!-- TEXT INPUT -->
    <div v-if="['text', 'email', 'password'].indexOf(inputType) > -1" :class="classes"
        :style="{ 'max-width': maxWidth ? maxWidth + '!important' : '', 'min-width': minWidth ? minWidth + '!important' : '' }">

        <div class="control-label" v-if="showLabel === true">
            <label-name :labelKey="label"></label-name>
        </div>

        <div :class="['control', { 'withInfoText': showInfoText }, icon != null ? 'has-icon has-icon-right' : '']">

                <div class="form-input-container" style="display: flex; align-items: center;">
                    <div style="flex: 1;">
                        <template v-if="isPointToComma == true">
                            <input
                                :id="getId"
                                :ref="getId"
                                :readonly="isReadOnly"
                                :class="[{ 'is-disabled': isReadOnly }, inputSize, 'input']"
                                :type="inputType"
                                :placeholder="placeholder"
                                v-bind:value="value"
                                v-pointToComma="value"
                                @input="updateData($event.target.value)">
                        </template>

                        <template v-else>

                            <input 
                                :id="getId"
                                :ref="getId"
                                :readonly="isReadOnly"
                                :class="[{ 'is-disabled': isReadOnly }, inputSize, 'input']"
                                :type="showPassword || revealPassword ? 'text' : inputType"
                                :placeholder="placeholder"
                                v-bind:value="value"
                                @input="updateData($event.target.value)"
                                :required="isRequired">

                            <!-- Password Reveal Icon -->
                            <template v-if="inputType === 'password'">
                                <span @click="togglePassword"
                                    style="position: absolute; right: 10px; top: 60%; transform: translateY(-50%); cursor: pointer; z-index: 2;"
                                    :title="showPassword ? 'Key verbergen' : 'Key anzeigen'">
                                    <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                                </span>
                            </template>

                        </template>

                        <template v-if="icon != null">
                            <i :class="['fa', icon]"></i>
                        </template>
                    </div>
                    <div class="form-input-action" v-if="$slots.button">
                        <slot name="button"></slot>
                    </div>
                </div>
            <slot></slot>
            <lang :labelKey="`${label}-INFO`" class="info" v-if="showInfoText"></lang>
        </div>

    </div>


    <!-- TEXTAREA -->
    <div v-else-if="inputType == 'textarea'"
        :class="['control', { 'is-horizontal': isHorizontal }, { 'isLeft': isLeft }, { 'isRight': isRight }, { 'is-fullwidth': fullWidth }]"
        :style="{ 'max-width': maxWidth ? maxWidth + '!important' : '', 'min-width': minWidth ? minWidth + '!important' : '' }">

        <div class="control-label" v-if="showLabel === true">
            <label-name :labelKey="label"></label-name>
        </div>

        <div :class="['control', { 'withInfoText': showInfoText }]">

            <div class="form-input-container" style="display: flex; align-items: flex-start;">
                <div style="flex: 1;">
                    <template v-if="textareaAutosize == true">

                        <textarea-autosize :placeholder="placeholder" :ref="label" v-bind:value="value"
                            :min-height="textareaMinHeight" :max-height="textareaMaxHeight"
                            :class="[{ 'is-disabled': isReadOnly }, inputSize, 'textarea']" @input="updateData">

                        </textarea-autosize>

                    </template>

                    <template v-else>

                        <textarea :ref="label" :id="getId" :class="[{ 'is-disabled': isReadOnly }, inputSize, 'textarea']"
                            type="text" :placeholder="placeholder" v-bind:value="value" :rows="textareaRows"
                            :maxLength="textareaMaxLength" @input="updateData($event.target.value)">

                        </textarea>

                        <!--
                        <p v-if="textareaMaxLength != null">Verbleibende Zeichen: <span class="charleft">{{ textareaMaxLength }}</span></p>
                        -->

                    </template>
                </div>
                <div class="form-input-action" v-if="$slots.button">
                    <slot name="button"></slot>
                </div>
            </div>
            <lang :labelKey="`${label}-INFO`" class="info" v-if="showInfoText"></lang>
        </div>

    </div>


    <!-- SELECT -->
    <div v-else-if="inputType == 'select'"
        :class="['control', { 'is-horizontal': isHorizontal }, { 'isLeft': isLeft }, { 'isRight': isRight }, { 'is-fullwidth': fullWidth }]"
        :style="{ 'max-width': maxWidth ? maxWidth + '!important' : '', 'min-width': minWidth ? minWidth + '!important' : '' }">

        <div class="control-label" v-if="showLabel === true">
            <label-name :labelKey="label"></label-name>
        </div>

        <div :class="['control', { 'withInfoText': showInfoText }]">

            <div class="form-input-container" style="display: flex; align-items: center;">
                <div style="flex: 1;">
                    <multiselect
                        ref="multiselect"
                        v-model="selectedOption" 
                        :deselect-label="deselectLabel" 
                        selectLabel=""
                        selectedLabel="" 
                        :loading="loading" 
                        :disabled="selectIsDisabled" 
                        :track-by="selectTrackBy"
                        :custom-label="getLabelName" 
                        :open-direction="selectOpenDirection"
                        :placeholder="placeholder || $t('SELECT_BOX-' + label + '-PLEASE_SELECT')" 
                        :options="options"
                        :searchable="selectIsSearchable" 
                        :allow-empty="selectAllowEmpty" 
                        :multiple="selectMultiple"
                        :internal-search="selectInternalSearch"
                        :close-on-select="selectCloseOnSelect"
                        :clear-on-select="selectClearOnSelect"
                        @input="onMultiselectInput"
                        @select="updateData"
                        @open="onDropdownOpen" 
                        @close="onDropdownClose"
                        @remove="onResetSelect" 
                        @search-change="$emit('search-change', $event)"
                        :class="['singleSelect', inputSize]">
                            <span slot="noResult">Ihre Suche ergab leider keine Treffer.</span>
                            <span slot="noOptions">Keine Einträge vorhanden</span>
                    </multiselect>
                </div>
                <div class="form-input-action" v-if="$slots.button">
                    <slot name="button"></slot>
                </div>
            </div>


        </div>

    </div>


    <!-- CUSTOM / SLOT ONLY -->
    <div v-else-if="inputType == 'custom'"
        :class="['control', { 'is-horizontal': isHorizontal }, { 'isLeft': isLeft }, { 'isRight': isRight }, { 'is-fullwidth': fullWidth }]"
        :style="{ 'max-width': maxWidth ? maxWidth + '!important' : '', 'min-width': minWidth ? minWidth + '!important' : '' }">
        
        <div class="control-label" v-if="showLabel === true">
            <label-name :labelKey="label"></label-name>
        </div>

        <div :class="['control', { 'withInfoText': showInfoText }]">
            <div class="form-input-container" style="display: flex; align-items: center;">
                <div style="flex: 1;">
                    <slot></slot>
                </div>
                <div class="form-input-action" v-if="$slots.button">
                    <slot name="button"></slot>
                </div>
            </div>
            <lang :labelKey="`${label}-INFO`" class="info" v-if="showInfoText"></lang>
        </div>
    </div>


    <!-- datePicker -->
    <div v-else-if="inputType == 'datepicker'"
        :class="['control', { 'is-horizontal': isHorizontal }, { 'isLeft': isLeft }, { 'isRight': isRight }, { 'is-fullwidth': fullWidth }]"
        :style="{ 'max-width': maxWidth ? maxWidth + '!important' : '', 'min-width': minWidth ? minWidth + '!important' : '' }">

        <div class="control-label" v-if="showLabel === true">
            <label-name :labelKey="label"></label-name>
        </div>

        <div :class="['control', { 'withInfoText': showInfoText }]">
            {{ value }}
            <flat-pickr @on-change="updateData" :placeholder="placeholder" class="form-control"
                :config="datePickerOptions" v-model="value"></flat-pickr>
        </div>

    </div>

    <!-- SWITCH / TOGGLE -->
    <div v-else-if="inputType == 'switch'"
        :class="['control', { 'is-horizontal': isHorizontal }, { 'isLeft': isLeft }, { 'isRight': isRight }, { 'is-fullwidth': fullWidth }]"
        :style="{ 'max-width': maxWidth ? maxWidth + '!important' : '', 'min-width': minWidth ? minWidth + '!important' : '' }">

        <div class="control-label" v-if="showLabel === true">
            <label-name :labelKey="label"></label-name>
        </div>

        <div :key="value" :class="['control', { 'withInfoText': showInfoText }]">
            <b-switch ref="switcher" :checked="value" :on-change="updateData" type="primary" size="small"
                :disabled="isReadOnly"></b-switch>
            <lang :labelKey="`${label}-INFO`" class="info" v-if="showInfoText"></lang>
        </div>

        <modal width="640" transition="fadeDown" cancel-text="abbrechen" ok-text="verstanden" :show-ok="true"
            title="Bestätigung" :backdrop-closable="false" :is-show="showConfirm"
            :on-ok="function () { onSwitchConfirm() }" :on-cancel="function () { onSwitchCancel() }" class="vcenter">

            <div class="has-text-centered">
                <p v-html="confirmText"></p>
            </div>

        </modal>
    </div>
    
    <!-- SLIDER -->
     <div v-else-if="inputType == 'slider'"
        :class="['control', { 'is-horizontal': isHorizontal }, { 'isLeft': isLeft }, { 'isRight': isRight }, { 'is-fullwidth': fullWidth }]"
        :style="{ 'max-width': maxWidth ? maxWidth + '!important' : '', 'min-width': minWidth ? minWidth + '!important' : '' }">
        
        <div class="control-label" v-if="showLabel === true">
            <label-name :labelKey="label"></label-name>
        </div>

        <div :class="['control number', { 'withInfoText': showInfoText }, icon != null ? 'has-icon has-icon-right' : '']" style="justify-content: center;">
            <input-number :min="minValue" :max="maxValue" :val="Number(value)" :on-change="updateData"></input-number>
            <lang :labelKey="`${label}-INFO`" class="info" v-if="showInfoText"></lang>
        </div>  
     </div>
     
</template>


<script>
import Vue from 'vue'

import VueTextareaAutosize from 'vue-textarea-autosize'
Vue.use(VueTextareaAutosize)


import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import { de } from 'flatpickr/dist/l10n/de.js';
de.firstDayOfWeek = 1; // Monday

export default {

    components: {
        flatPickr,
    },

    props: {

        value: null,

        inputType: {
            type: String,
            default: 'text'
        },
        selectOptions: {
            type: Array,
            default: []
        },
        selectFieldName: {
            type: String,
            default: 'name'
        },
        selectTrackBy: {
            type: String,
            default: 'id'
        },
        selectCustomLabel: {
            type: String,
            default: null
        },
        selectIsSearchable: {
            type: Boolean,
            default: false
        },
        selectIsDisabled: {
            type: Boolean,
            default: false
        },
        selectAllowEmpty: {
            type: Boolean,
            default: true
        },
        selectOpenDirection: {
            type: String,
            default: 'auto'
        },
        selectMultiple: {
            type: Boolean,
            default: false
        },
        selectInternalSearch: {
            type: Boolean,
            default: true
        },
        selectCloseOnSelect: {
            type: Boolean,
            default: true
        },
        selectClearOnSelect: {
            type: Boolean,
            default: true
        },

        selectReturnSingleValue: {
            type: [String, Boolean],
            default: false,
        },
        loading: {
            type: Boolean,
            default: false
        },

        showInfoText: {
            type: Boolean,
            default: false,
        },
        showLabel: {
            type: Boolean,
            default: true
        },
        showLabelInOptions: {
            type: Boolean,
            default: true
        },
        showPleaseSelectField: {
            type: Boolean,
            default: true
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        isRequired: {
            type: Boolean,
            default: false
        },
        isPointToComma: {
            type: Boolean,
            default: false
        },
        maxWidth: {
            type: [String, Number],
            default: null,
        },
        minWidth: {
            type: [String, Number],
            default: null,
        },
        inputSize: {
            type: String,
            default: 'is-default'
        },
        isReadOnly: {
            type: Boolean,
            default: false
        },
        isHorizontal: {
            type: Boolean,
            default: false
        },
        icon: {
            type: String,
            default: null
        },
        textareaAutosize: {
            type: Boolean,
            default: false
        },
        textareaMaxLength: {
            type: Number,
            default: null
        },
        textareaMinHeight: {
            type: Number,
            default: 50
        },
        textareaMaxHeight: {
            type: Number,
            default: 500
        },
        textareaRows: {
            type: Number,
            default: 4
        },
        isLeft: {
            type: Boolean,
            default: false,
        },
        isRight: {
            type: Boolean,
            default: false,
        },
        confirmText: {
            type: String,
            default: null
        },
        revealPassword: {
            type: Boolean,
            default: false
        },

        minValue: {
            type: Number,
            default: 1
        },
        maxValue: {
            type: Number,
            default: 100
        },
        fullWidth: {
            type: Boolean,
            default: false
        },

    },

    data: function () {

        return {
            // selectedOption: null,
            // deselectLabel: '',
            deselectLabel: 'Klick um abzuwählen',
            options: [],
            showConfirm: false,
            pendingValue: null,
            showPassword: false, // <--- hinzufügen
        }

    },

    created() {
        if (this.inputType == "select") {
            this.setOptions();
        }
    },

    mounted() {

    },

    computed: {

        getId() {

            if (this.showLabel === true) {
                return this.label;
            } else {
                return "id-" + Math.floor((Math.random() * 10000000));
            }
        },

        classes() {

            var ret = [];

            ret.push('control');
            if (this.isHorizontal) ret.push('is-horizontal');
            if (this.isLeft) ret.push('isLeft');
            if (this.isRight) ret.push('isRight');
            if (this.fullWidth) ret.push('is-fullwidth');
            if (!this.isLeft && !this.isRight) ret.push('wide');

            return ret.join(' ');

        },

        selectedOption: {
            // getter
            get() {

                if (this.selectReturnSingleValue) {
                    let val = this.value && this.value.value ? this.value.value : this.value;
                    return this.options.find((o) => o.value == val);
                }
                else {
                    return this.value;
                }
            },
            // setter
            set(newValue) {
                this.value = newValue;
            }
        }

    },

    methods: {

        onMultiselectInput(data) {
            if (this.selectMultiple) {
                this.$emit('input', data);
            }
        },

        togglePassword() {
            this.showPassword = !this.showPassword;
        },

        onResetSelect(removedOption, id) {
            this.$emit('input', null);
        },

        getLabelName(option) {

            if (this.showLabelInOptions == true) {
                return this.$t('SELECT_BOX-' + this.label + '-' + option[this.selectFieldName].toUpperCase());
            } else {
                if (this.selectCustomLabel != null) {
                    return option.customLabel;
                } else {
                    return option[this.selectFieldName];
                }
            }

        },

        updateData(data) {

            if (this.inputType == 'select') {

                if (this.selectReturnSingleValue) {
                    this.$emit('input', data.value ? data.value : null);
                } else {
                    this.$emit('input', data);
                }
            }

            else if (this.inputType == 'switch') {

                this.showConfirm = false;

                if (
                    this.confirmText && this.confirmText.length > 0 && this.value === true && data === false
                ) {
                    this.pendingValue = data;
                    this.showConfirm = true;
                } else {
                    this.$emit('input', data);
                }

            }
            else if (this.inputType == 'datepicker') {

            }
            else {

                // console.log(data);
                if (this.inputType != 'textarea' && this.isPointToComma == true) {
                    data = (data.toString()).replace(",", ".");
                }

                this.$emit('input', data); //data.trim()

            }

        },

        onSwitchConfirm() {
            this.$emit('input', this.pendingValue);
            this.pendingValue = null;
            this.showConfirm = false;
        },

        onSwitchCancel() {
            this.$refs.switcher.on = true; // reset switcher
            this.$emit('input', !this.pendingValue);
            this.pendingValue = null;
            this.showConfirm = false;
        },

        onDropdownOpen(data) {
            const el = this.$refs.multiselect.$el;
            // Entferne die Klasse sofort, damit CSS das Dropdown versteckt
            el.classList.remove('multiselect--positioned');

            this.$nextTick(() => {
                const menu = el.querySelector('.multiselect__content-wrapper');
                if (!menu) return;

                // Take menu out of flow immediately so scrollbars collapse
                menu.style.position = 'fixed';
                menu.style.bottom = 'auto'; // Override vue-multiselect original 'bottom: 100%'

                const dummy = document.createElement('div');
                dummy.style.position = 'fixed';
                dummy.style.left = '0px';
                dummy.style.top = '0px';
                dummy.style.margin = '0px';
                dummy.style.padding = '0px';
                dummy.style.border = 'none';
                el.appendChild(dummy);

                let isFirstFrame = true;

                const updatePosition = () => {
                    // Stop loop if closed and clean up
                    if (!el.classList.contains('multiselect--active')) {
                        if (dummy.parentNode) dummy.parentNode.removeChild(dummy);
                        return;
                    }

                    const rect = el.getBoundingClientRect();
                    const fixedRect = dummy.getBoundingClientRect();
                    
                    const offsetX = fixedRect.left;
                    const offsetY = fixedRect.top;

                    menu.style.left = `${rect.left - offsetX}px`;
                    menu.style.width = `${rect.width}px`;
                    menu.style.bottom = 'auto'; // Ensure bottom is overridden

                    if (el.classList.contains('multiselect--above')) {
                        menu.style.top = `${rect.top - offsetY - menu.offsetHeight}px`;
                    } else {
                        menu.style.top = `${rect.bottom - offsetY}px`;
                    }

                    if (isFirstFrame) {
                        el.classList.add('multiselect--positioned');
                        isFirstFrame = false;
                    }

                    requestAnimationFrame(updatePosition);
                };

                // Start loop
                requestAnimationFrame(updatePosition);
            });

            this.$emit('open', data)
        },

        onDropdownClose() {
            const el = this.$refs.multiselect ? this.$refs.multiselect.$el : null;
            if (el) {
                el.classList.remove('multiselect--positioned');
                const menu = el.querySelector('.multiselect__content-wrapper');
                if (menu) {
                    menu.style.position = '';
                    menu.style.width = '';
                    menu.style.left = '';
                    menu.style.top = '';
                }
            }
        },

        getScrollbarWidth() {
            const outer = document.createElement('div');
            outer.style.visibility = 'hidden';
            outer.style.overflow = 'scroll';
            outer.style.msOverflowStyle = 'scrollbar';
            document.body.appendChild(outer);
            const inner = document.createElement('div');
            outer.appendChild(inner);
            const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth);
            outer.parentNode.removeChild(outer);
            return scrollbarWidth;
        },

        setOptions() {
            this.options = [];

            // if it is not allowed to deselect an entry,
            // we do not wann show the "Klick um abzuwählen" message ...
            if (this.selectAllowEmpty == false) {
                this.deselectLabel = "";
            }

            for (var key in this.selectOptions) {

                // skip loop if the property is from prototype
                if (!this.selectOptions.hasOwnProperty(key)) {
                    continue;
                }
                var obj = this.selectOptions[key];

                let newSelectObj = { ...this.selectOptions[key] };
                newSelectObj[this.selectFieldName] = obj[this.selectFieldName];
                newSelectObj.customLabel = this.selectCustomLabel != null ? this.selectCustomLabel.split('.').reduce((a, b) => a[b], obj) : null;
                this.options.push(newSelectObj);
            }
        }

    },

    watch: {
        selectOptions: {
            handler(newValue) {
                if (this.inputType == "select") {
                    this.setOptions();
                }
            },
            deep: true
        }
    },

    directives: {

        pointToComma(el, binding) {
            el.value = ([el.value].toString()).replace(".", ",");
        }

    }

}


</script>


<style lang="scss" scoped>

.control {

    &.number {
        ::v-deep .blu-ipt-number {
            display: flex !important;
            flex-direction: row !important;
            justify-content: flex-start !important;
            margin-bottom: 0 !important;
           
            input {
                width: auto !important;
            }
            a.button {
                width: auto !important;
                border: 1px solid #dbdbdb;
                padding: 8px !important;
                display: flex !important;
                span.icon {
                    margin: 0 !important;
                }
            }
        }

    }

    .textarea {
        min-height: auto;
        max-height: auto;
        height: auto;
    }

    ::v-deep .singleSelect.multiselect--active.multiselect--positioned .multiselect__content-wrapper {
        opacity: 1 !important;
        transition: none !important;
    }

    .form-input-action {
        margin-left: 10px;

        ::v-deep .button {
            height: 40px;
            width: 44px;
            padding: 0;
            border: 1px solid #dbdbdb;
            background: #fff;
            color: #363636;
            box-shadow: none;
            display: flex;
            justify-content: center;
            align-items: center;

            &:hover {
                background: #f5f5f5;
            }

            &.is-add-button {
                background: #e0e0e0;
                &:hover {
                    background: #d5d5d5;
                }
            }
        }
    }

}
</style>

<style lang="scss">

.singleSelect {
    font-size: 12px !important;
    min-width: 220px !important;
    min-height: 32px !important;
}

.singleSelect:not(:last-child) {
    margin-right: 5px !important;
}

.singleSelect .multiselect__select {
    position: absolute !important;
    z-index: 999 !important;
    width: 24px !important;
    height: auto !important;
    padding: 0 !important;
    right: 1px !important;
    top: 1px !important;
    bottom: 1px !important;
}

.singleSelect .multiselect__select:before {
    position: relative !important;
    right: 0 !important;
    top: 60% !important;
    color: #999 !important;
    margin-top: 4px !important;
    border-style: solid !important;
    border-width: 5px 5px 0 !important;
    border-color: #999 transparent transparent !important;
    content: "";
}

.singleSelect .multiselect__tags {
    min-height: 32px !important;
    padding: 0 40px 0 8px !important;
    display: flex;
    align-items: center;
    line-height: 21px;
}

.singleSelect .multiselect__spinner {
    position: absolute;
    right: 1px;
    left: auto;
    top: auto;
    width: 24px;
    height: 24px;
    background: transparent;
    display: block;
}

.singleSelect .multiselect__input,
.singleSelect .multiselect__single {
    font-size: 13px;
    margin: 0 !important;
    padding-left: 0 !important;
    white-space: nowrap;
    line-height: inherit;
    line-height: 21px;
}

.multiselect__placeholder {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 13px;
    line-height: 21px;
}

.singleSelect .option__statusIndicator {
    height: 15px !important;
    width: 15px !important;
    margin-right: 6px !important;
    display: inline-block !important;
    flex: 1 0 15px;
    max-width: 15px;
    border: .5px solid #bbb;
    border-radius: 2px;
}

.singleSelect .multiselect__tag {
    position: relative !important;
    display: inline-block !important;
    color: #fff !important;
    background: #41b883 !important;
    margin-bottom: 0 !important;
    vertical-align: top !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    ;
    max-width: 100% !important;
    text-overflow: ellipsis !important;
    margin: 2px 8px 2px 0 !important;
    font-size: 12px;
}


/* dropdown */
.singleSelect .multiselect__content-wrapper {
    border-radius: 2px;
    border-top: 1px solid transparent;
    overflow-x: hidden;
}

.singleSelect.multiselect--above .multiselect__content-wrapper {
    border-bottom: 1px solid transparent !important;
}

.singleSelect.multiselect--active.multiselect--above .multiselect__content-wrapper {
    border-top: 1px solid #e8e8e8 !important;
}

.singleSelect ul.multiselect__content {
    margin: 0 !important;
    padding: 0 !important;
}

.singleSelect ul.multiselect__content li>span {
    white-space: nowrap;
    font-size: 13px !important;
}

.singleSelect ul.multiselect__content li>span.multiselect__option:after {
    font-size: 11px !important;
}


.singleSelect .multiselect__option {
    display: flex;
    align-items: center;
    text-decoration: none;
    text-transform: none;
    position: relative;
    cursor: pointer;
    white-space: nowrap;
    padding: 6px 12px;
    min-height: 0;
}

.singleSelect .multiselect__option:after {
    top: 0 !important;
    bottom: 0 !important;
    right: 0 !important;
    position: absolute !important;
    padding-right: 8px;
    display: flex;
    align-items: center;
}

.singleSelect .option__title {
    line-height: 1;
}

.multiselect--active {
    z-index: 99999 !important;
}

/*
.singleSelect.multiselect--active .multiselect__content-wrapper {
    position: fixed !important;
    opacity: 0 !important;
    transition: none !important;
}

.singleSelect.multiselect--active.multiselect--positioned .multiselect__content-wrapper {
    opacity: 1 !important;
    transition: none !important;
}
*/

.multiselect__content {
    width: 100%;
}

.multiselect__content-wrapper ul.multiselect__content {
    margin: 0;
}

.multiselect__content-wrapper ul.multiselect__content li {
    margin: 0;
    padding: 0;
}

/* tags */
.multiselect__tags {
    border-radius: 3px !important;
    border: 1px solid #dbdbdb !important;
    white-space: normal;
}

.multiselect .multiselect__tag {
    background-color: #F5F7F9 !important;
    color: #222 !important;
}

.multiselect__tag-icon:after {
    color: #aaa !important;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
    background-color: #ddd !important;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover:after {
    color: #000 !important;
}

/* hover for options */
.multiselect__option--highlight {
    background-color: #F5F7F9 !important;
    color: #222 !important;
}

.multiselect__option--highlight:after {
    background-color: #F5F7F9 !important;
    color: #222 !important;
}


.multiselect--disabled .multiselect__current,
.multiselect--disabled .multiselect__select,
.multiselect__option--disabled {
    /*background: none;*/
}

.multiselect--disabled .multiselect__tags,
.multiselect--disabled .multiselect__single {
    background: #ededed !important;
}

.control-label {
    padding: 0 0 3px 0;
    margin: 0;
}

.control:not(:last-child) {
    margin-bottom: 2px;
}

.control>.control {

    display: flex;

    &.withInfoText {
        flex-direction: column;
    }

    .info {
        margin-top: 6px;
        font-size: 12px;
        line-height: 1.4;
        color: #aaa;
        white-space: normal;
    }
}

.control.is-fullwidth > .control {
    max-width: 100% !important;
}

.control.isLeft {
    float: left;
    width: 48.5%;
}

.control.isRight {
    float: right;
    width: 48.5%;
    min-width: 48.5%;
}

.control.wide {
    clear: both;
    width: 100%;
    min-width: 48.5%;

    .control>div {
        width: 100%;
    }
}

</style>